import { motion } from 'framer-motion';
import { useState } from 'react';
import type { MediaCard as MediaCardType } from '../../lib/workoutCards';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ImageLightbox } from '../ui/ImageLightbox';

interface MediaCardProps {
  card: MediaCardType;
}

export function MediaCard({ card }: MediaCardProps) {
  const { media } = card;
  const url = media.downloadUrl || media.externalUrl;
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Extract video ID from YouTube/Vimeo URLs
  const getEmbedUrl = (url: string): string | null => {
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

    return null;
  };

  const embedUrl = url ? getEmbedUrl(url) : null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className="h-full"
    >
      <Card padding="lg" variant="elevated" className="h-full flex flex-col justify-center space-y-4">
        <div className="text-center">
          <Badge variant="default" size="md" className="mb-3">
            Reference Media
          </Badge>
          <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {card.exerciseName}
          </p>
        </div>

        <div className="flex-1 flex items-center justify-center">
          {embedUrl ? (
            <iframe
              src={embedUrl}
              className="w-full max-w-3xl aspect-video rounded-lg shadow-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : media.type === 'video' ? (
            <video
              src={url}
              controls
              className="max-w-full max-h-[60vh] rounded-lg shadow-lg"
              playsInline
            />
          ) : media.type === 'image' ? (
            <>
              <img
                src={url}
                alt={media.caption || card.exerciseName}
                className="max-w-full max-h-[60vh] rounded-lg shadow-lg object-contain cursor-pointer hover:opacity-90 transition"
                onClick={() => setLightboxOpen(true)}
              />
              <ImageLightbox
                src={url || ''}
                alt={media.caption || card.exerciseName}
                isOpen={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
              />
            </>
          ) : (
            <a href={url} target="_blank" rel="noopener noreferrer">
              <Button>View Document</Button>
            </a>
          )}
        </div>

        {media.caption && (
          <p className="text-sm text-gray-600 dark:text-slate-400 text-center">
            {media.caption}
          </p>
        )}
      </Card>
    </motion.div>
  );
}
